import React from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {BioMatchEnrollmentService, type BioMatchEnrollment} from '../services/bioMatchEnrollmentService';
import SectionHeader from '@/shared/components/SectionHeader';
import {dollars} from '@/shared/utils/money';

function actionError(e: unknown): string {
    return (e as {data?: {error?: string}})?.data?.error ?? (e as Error)?.message ?? 'Action failed.';
}

type SubAction = 'cancel' | 'pause' | 'resume' | 'reschedule';

const ManageSubscriptionsView: React.FC = () => {
    const qc = useQueryClient();
    const {data, isLoading, isError} = useQuery({
        queryKey: ['my-subscriptions'],
        queryFn: () => BioMatchEnrollmentService.listMine(true),
        staleTime: 30_000,
    });

    const actMut = useMutation({
        mutationFn: (v: {id: number; action: SubAction; body: {reason?: string; next_test_due?: string}}) =>
            BioMatchEnrollmentService.subscriptionAction(v.id, v.action, v.body),
        onSuccess: () => {
            toast.success('Subscription updated.');
            qc.invalidateQueries({queryKey: ['my-subscriptions']});
        },
        onError: (e) => toast.error(actionError(e)),
    });

    const act = (id: number, action: SubAction) => {
        if (action === 'reschedule') {
            const d = window.prompt('New next-test date (YYYY-MM-DD):', '');
            if (d === null || d.trim() === '') return;
            actMut.mutate({id, action, body: {next_test_due: d.trim()}});
            return;
        }
        if (action === 'cancel' && !window.confirm('Cancel this subscription? Your recurring kits will stop.')) return;
        actMut.mutate({id, action, body: {}});
    };

    return (
        <div className="p-6 space-y-4">
            <SectionHeader title="My Subscriptions" />
            <p className="text-sm text-gray-500">Manage your recurring lab kits — pause, resume, reschedule, or cancel anytime.</p>
            {isLoading && <p className="text-gray-500">Loading…</p>}
            {isError && <p className="text-mld-status-high">Could not load your subscriptions.</p>}
            {data && data.length === 0 && <p className="text-gray-500">You have no subscriptions yet.</p>}
            {data && data.length > 0 && (
                <div className="space-y-3">
                    {data.map((s: BioMatchEnrollment) => (
                        <div key={s.id} className="bg-white rounded-2xl border border-mld-panel-border shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="font-semibold text-mld-text-primary">{s.recommendation_name}</p>
                                <p className="text-sm text-gray-500">
                                    {s.status} · next {s.next_test_due ?? '—'} · {dollars(s.patient_amount_cents)}
                                </p>
                            </div>
                            {s.status !== 'cancelled' && s.status !== 'completed' && (
                                <div className="flex flex-wrap gap-2">
                                    {s.status === 'active' && (
                                        <button type="button" onClick={() => act(s.id, 'pause')}
                                            className="rounded-md border border-gray-300 px-3 py-1 text-sm">Pause</button>
                                    )}
                                    {s.status === 'paused' && (
                                        <button type="button" onClick={() => act(s.id, 'resume')}
                                            className="rounded-md border border-gray-300 px-3 py-1 text-sm">Resume</button>
                                    )}
                                    <button type="button" onClick={() => act(s.id, 'reschedule')}
                                        className="rounded-md border border-gray-300 px-3 py-1 text-sm">Reschedule</button>
                                    <button type="button" onClick={() => act(s.id, 'cancel')}
                                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-mld-status-high">Cancel</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManageSubscriptionsView;
